import { onMessage } from "firebase/messaging";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Loader from "./components/Loader";
import { generateToken, messaging } from "./firebase";
import BeforeLoginRoute from "./guards/BeforeLoginRoute";
import ProtectedRoute from "./guards/ProtectedRoute";
import AuthLayout from "./Layout/AuthLayout";
import UserLayout from "./Layout/UserLayout";
import AddCorporate from "./pages/AddCorporates";
import AddCourtManagement from "./pages/AddCourtManagement";
import Booking from "./pages/Booking";
import BookingDetail from "./pages/BookingDetailPage";
import Corporate from "./pages/Corporates";
import Coupons from "./pages/Coupons";
import CourtManagement from "./pages/CourtManagement";
import Dashboard from "./pages/Dashboard";
import EditCourtManagement from "./pages/EditCourtManagement";
import Locations from "./pages/Locations";
import Login from "./pages/Login";
import NotificationCenter from "./pages/NotificationCenter";
import Payments from "./pages/Payments";
import Profile from "./pages/Profile";
import UserDetail from "./pages/UserDetail";
import Users from "./pages/Users";
import { setFcmToken } from "./store/authSlice";
import useApi from "./useApi";
import fetchAllData from "./useApi/all-dropdown-api";
// Map of route paths to components
const routeComponentMap = {
  bookings: Booking,
  payments: Payments,
  profile: Profile,
  locations: Locations,
  "court-management": CourtManagement,
  corporates: Corporate,
};

// Special route components that need additional sub-routes
const specialRoutes = {
  bookings: [
    {
      path: ":id",
      element: BookingDetail,
    },
  ],
  "court-management": [
    {
      path: "add-court",
      element: AddCourtManagement,
    },
    {
      path: ":id",
      element: BookingDetail,
    },
  ],
  "corporates": [
    {
      path: "add-corporates",
      element: AddCorporate,
    },
  ],
};

function App() {
  const { isLogin, menu, fcmToken } = useSelector((state) => state?.auth);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  // Track if we're showing the no access message
  const [showingNoAccessMessage, setShowingNoAccessMessage] = useState(false);


  const {
    getBookingsAdmin,
    getCourts,
    getAdminLocations,
    getLocations,
    getAllBlockedSlots,
    addBooking,
    refundAndCancel,
    paymentReceive,
    confirmBooking,
  } = useApi();

  // Check if user has any accessible routes
  const hasAccessibleRoutes = () => {
    if (!menu || !Array.isArray(menu)) return false;
    return menu.some((item) => item.router && routeComponentMap[item.router]);
  };

  // Handle users with no access - this will only run once per session
  useEffect(() => {
    // Only run this logic if the user is logged in and has no access
    if (isLogin && !hasAccessibleRoutes() && !showingNoAccessMessage) {
      // Show the no access message for 3 seconds then log out
      setShowingNoAccessMessage(true);

      // Set a timeout to log out after showing the message
      const timer = setTimeout(() => {
        // Clear auth data from localStorage
        localStorage.removeItem("auth");
        localStorage.removeItem("token");

        // Force a page refresh to clear the Redux state
        window.location.href = "/login";
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isLogin, menu, showingNoAccessMessage]);

  useEffect(() => {
    if (isLogin) {
      fetchAllData(
        dispatch,
        getLocations,
        setLoading
      );
    }
  }, [isLogin]);

  if (process.env.REACT_APP_ENVIRONMENT == "production") {
    console.log = () => { };
  }

  const notificationHandler = async () => {
    const deviceToken = await generateToken();
    dispatch(setFcmToken(deviceToken));
    console.log(deviceToken)
    // dispatch(fcmHandler(deviceToken));
    onMessage(messaging, (payload) => {
      toast.info(payload.notification.body)
      console.log('Message received. ', payload);
    });
  }

  useEffect(() => {
    notificationHandler();
  }, []);

  // Check if user has access to a specific route
  const hasAccess = (routePath) => {
    if (!menu || !Array.isArray(menu)) return false;
    return menu.some((item) => item.router === routePath);
  };

  // Get all accessible routes from menu
  const getAccessibleRoutes = () => {
    if (!menu || !Array.isArray(menu)) return [];
    return menu.map((item) => item.router).filter((router) => router);
  };
  // If we're showing the no access message, render it directly instead of using routes
  if (isLogin && showingNoAccessMessage) {
    window.location.href = "/dashboard"
    toast.error("Unauthorized.");
  }



  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <ToastContainer />
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route
                path="/"
                element={<BeforeLoginRoute element={<AuthLayout />} />}
              >
                <Route index element={<Navigate to="/login" />} />
                <Route
                  path="login"
                  element={<BeforeLoginRoute element={<Login />} />}
                />
              </Route>

              <Route
                path={`/corporates`}
                element={<ProtectedRoute element={<UserLayout />} />}
              >
                {/* Main route component */}
                <Route index element={<Corporate />} />

                {/* Special sub-routes if they exist for this route */}
              </Route>
              <Route
                path={`/add-corporates`}
                element={<ProtectedRoute element={<UserLayout />} />}
              >
                {/* Main route component */}
                <Route index element={<AddCorporate />} />

                {/* Special sub-routes if they exist for this route */}
              </Route>
              {/* Dynamic Menu Routes with their special sub-routes */}

              <Route
                path={`/users`}
                element={<ProtectedRoute element={<UserLayout />} />}
              >
                {/* Main route component */}
                <Route index element={<Users />} />

                {/* Special sub-routes if they exist for this route */}
              </Route>
              <Route
                path={`/users/:id`}
                element={<ProtectedRoute element={<UserLayout />} />}
              >
                {/* Main route component */}
                <Route index element={<UserDetail />} />

                {/* Special sub-routes if they exist for this route */}
              </Route>
              {isLogin &&
                menu &&
                Array.isArray(menu) &&
                menu.map((menuItem) => {
                  console.log("menuItems", menuItem);
                  if (menuItem.router && routeComponentMap[menuItem.router]) {
                    const Component = routeComponentMap[menuItem.router];

                    // Create the main route with potential sub-routes
                    return (
                      <Route
                        key={menuItem.accessID}
                        path={`/${menuItem.router}`}
                        element={<ProtectedRoute element={<UserLayout />} />}
                      >
                        {/* Main route component */}
                        <Route index element={<Component />} />

                        {/* Special sub-routes if they exist for this route */}
                        {specialRoutes[menuItem.router]?.map(
                          (subRoute, index) => (
                            <Route
                              key={`${menuItem.accessID}-${index}`}
                              path={subRoute.path}
                              element={
                                <ProtectedRoute
                                  element={<subRoute.element />}
                                />
                              }
                            />
                          )
                        )}
                      </Route>
                    );
                  }
                  return null;
                })}

              {/* Fallback Route - redirect to root path if logged in with accessible routes */}
              <Route
                path="/dashboard"
                element={<ProtectedRoute element={<UserLayout />} />}
              >
                <Route index element={<Dashboard />} />
              </Route>
              <Route path={`/coupons`}   element={<ProtectedRoute element={<UserLayout />} />}>
                {/* Main route component */}
                <Route index element={<Coupons />} />
              </Route>
              <Route
                path={`/corporates/edit-corporate/:id`}
                element={<ProtectedRoute element={<UserLayout />} />}
              >
                {/* Main route component */}
                <Route index element={<AddCorporate />} />
              </Route>

              <Route
                path={`/court-management/edit-court/:id`}
              element={<ProtectedRoute element={<UserLayout />} />}
              >
                {/* Main route component */}
                <Route index element={<EditCourtManagement />} />
              </Route>

              <Route path={`/announcements`} element={<ProtectedRoute element={<UserLayout />} />}>
                {/* Main route component */}
                <Route index element={<ProtectedRoute element={<NotificationCenter />} />} />
              </Route>
              <Route
                path={`/corporates/add-corporates`}
                element={<ProtectedRoute element={<UserLayout />} />}
              >
                {/* Main route component */}
                <Route index element={<AddCorporate />} />
              </Route>
              <Route
                path="*"
                element={
                  isLogin ? (
                    hasAccessibleRoutes() ? (
                      <Navigate to="/" />
                    ) : null // Don't redirect - the no access logic will handle this
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />
            </Routes>
          </BrowserRouter>
        </>
      )}
    </>
  );
}

export default App;
